import type { FiltersState } from '../../hooks/useFilters';

type AvitoExperienceKey = 'avitoExperienceMin' | 'avitoExperienceMax';

const AVITO_EXPERIENCE_LIMIT = 50;

export function hasAvitoExperienceValue(value: unknown): boolean {
  return value !== undefined && value !== null && String(value).trim() !== '';
}

export function normalizeAvitoExperienceValue(value: unknown): string {
  if (!hasAvitoExperienceValue(value)) return '';

  const digits = String(value).replace(/\D/g, '');
  if (!digits) return '';

  return String(Math.min(Number(digits), AVITO_EXPERIENCE_LIMIT));
}

export function normalizeAvitoExperienceRange(
  filters: Partial<Pick<FiltersState, AvitoExperienceKey>>,
): Pick<FiltersState, AvitoExperienceKey> {
  const min = normalizeAvitoExperienceValue(filters.avitoExperienceMin);
  const max = normalizeAvitoExperienceValue(filters.avitoExperienceMax);

  return {
    avitoExperienceMin: min,
    avitoExperienceMax: hasAvitoExperienceValue(min)
      && hasAvitoExperienceValue(max)
      && Number(min) > Number(max)
      ? ''
      : max,
  };
}

export function buildAvitoExperienceInputPatch(
  key: AvitoExperienceKey,
  rawValue: unknown,
  filters: Partial<Pick<FiltersState, AvitoExperienceKey>>,
): Partial<Pick<FiltersState, AvitoExperienceKey>> {
  const normalizedValue = normalizeAvitoExperienceValue(rawValue);
  const current = normalizeAvitoExperienceRange(filters);
  const next = {
    ...current,
    [key]: normalizedValue,
  };
  const patch: Partial<Pick<FiltersState, AvitoExperienceKey>> = {
    [key]: normalizedValue,
  };

  if (
    key === 'avitoExperienceMin'
    && hasAvitoExperienceValue(next.avitoExperienceMin)
    && hasAvitoExperienceValue(next.avitoExperienceMax)
    && Number(next.avitoExperienceMin) > Number(next.avitoExperienceMax)
  ) {
    patch.avitoExperienceMax = '';
  }

  if (
    key === 'avitoExperienceMax'
    && hasAvitoExperienceValue(next.avitoExperienceMin)
    && hasAvitoExperienceValue(next.avitoExperienceMax)
    && Number(next.avitoExperienceMin) > Number(next.avitoExperienceMax)
  ) {
    patch.avitoExperienceMin = '';
  }

  return patch;
}
